import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { components } from '@/api/types'

export type AdminPhoto = NonNullable<components['schemas']['AdminPhoto']>

export type PhotoModerationDecision = 'approved' | 'rejected' | 'needs_review'

export const USER_PHOTOS_QUERY_KEY = ['admin', 'user-photos'] as const

/**
 * `GET /admin/users/{ulid}/photos` (DEC-MOD-007).
 *
 * A felhasználó fotóinak admin-nézete — a help-requestből indított
 * moderáláshoz (a moderálandó photo public_id-t innen kapja az admin).
 * A Laravel resource-collection `data`-mezőjét manuálisan extractáljuk
 * (mint a többi admin lista).
 */
export function useUserPhotos(userUlid: string | null) {
  return useQuery({
    queryKey: [...USER_PHOTOS_QUERY_KEY, userUlid] as const,
    queryFn: async (): Promise<AdminPhoto[]> => {
      const { data, error } = await apiClient.GET('/admin/users/{id}/photos', {
        params: { path: { id: userUlid ?? '' } },
      })
      if (error) throw error
      const envelope = data as { data?: AdminPhoto[] } | undefined
      return envelope?.data ?? []
    },
    enabled: !!userUlid,
  })
}

interface ModeratePhotoVariables {
  // DEC-SECURITY-002 §Tier 5 — opaque photo public_id ULID.
  id: string
  decision: PhotoModerationDecision
  reason?: string | null
}

/**
 * `PATCH /admin/photos/{id}/moderate` (DEC-ADMIN-008).
 *
 * 3 decision érték: approved (Ready), rejected (Rejected), needs_review
 * (Pending). Minden flip a `photo_moderation_statuses` history-ba ír egy
 * sort + a `photos.status`-t flip-eli. Terminal döntéseken (approve /
 * reject) a `moderation_actions` row is kerül (audit-trail).
 *
 * A reason opcionális (max 1000 char). Sikerre invalidate a felhasználó
 * fotó-listáját (DEC-MOD-007) + a moderation-actions cache-t.
 */
export function useModeratePhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, decision, reason }: ModeratePhotoVariables): Promise<void> => {
      const body: { decision: PhotoModerationDecision; reason?: string | null } = { decision }
      if (reason != null) body.reason = reason
      const { error } = await apiClient.PATCH('/admin/photos/{id}/moderate', {
        params: { path: { id } },
        body,
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_PHOTOS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}
